import { useEffect, useLayoutEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import AccountName from '../AccountName'
import PrivateValue from '../PrivateValue'

const finite = value => value != null && value !== '' && Number.isFinite(Number(value))
const money = value => finite(value) ? Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'
const signed = value => finite(value) ? `${value >= 0 ? '+' : ''}${money(value)}` : '—'
const percent = value => finite(value) ? `${value >= 0 ? '+' : ''}${Number(value).toFixed(2)}%` : '—'
const qty = value => finite(value) ? Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 }) : '—'
const color = value => value > 0 ? 'text-forest' : value < 0 ? 'text-ember' : 'text-inkMuted'

// A portal keeps details visible when the card is resized short or scrolled.
export function DetailValue({ label, value, children, colorClass = 'text-ink' }) {
  const trigger = useRef(null)
  const popup = useRef(null)
  const dismiss = useRef(null)
  const tooltipId = useId()
  const cancelDismiss = () => clearTimeout(dismiss.current)
  const scheduleDismiss = () => { cancelDismiss(); dismiss.current = setTimeout(() => setOpen(false), 150) }
  useEffect(() => () => clearTimeout(dismiss.current), [])
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ left: 0, top: 0 })
  useLayoutEffect(() => {
    if (!open) return
    const rect = trigger.current.getBoundingClientRect()
    const box = popup.current.getBoundingClientRect()
    setPosition({ left: Math.max(8, Math.min(rect.right - box.width, window.innerWidth - box.width - 8)),
      top: rect.bottom + box.height + 8 < window.innerHeight ? rect.bottom + 4 : Math.max(8, rect.top - box.height - 4) })
  }, [open])
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const key = event => { if (event.key === 'Escape') close() }
    const outside = event => { if (!trigger.current?.contains(event.target) && !popup.current?.contains(event.target)) close() }
    window.addEventListener('keydown', key)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    window.addEventListener('blur', close)
    window.addEventListener('pointerdown', outside)
    return () => {
      window.removeEventListener('keydown', key); window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close); window.removeEventListener('blur', close); window.removeEventListener('pointerdown', outside)
    }
  }, [open])
  return <>
    <button ref={trigger} type="button" className={`text-right font-normal tabular-nums rounded outline-none focus-visible:ring-2 focus-visible:ring-ink/40 ${colorClass}`}
      aria-label={`${label} details`} aria-expanded={open} aria-describedby={open ? tooltipId : undefined}
      onMouseEnter={() => { cancelDismiss(); setOpen(true) }} onMouseLeave={scheduleDismiss}
      onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={() => setOpen(true)}>
      {label === 'Qty' ? value : <PrivateValue>{value}</PrivateValue>}
    </button>
    {open && createPortal(<div ref={popup} id={tooltipId} role="tooltip" aria-label={`${label} breakdown`} onMouseEnter={cancelDismiss} onMouseLeave={scheduleDismiss}
      className="fixed z-[100] rounded-lg border border-border bg-canvas p-3 shadow-lg font-sans text-xs text-ink"
      style={{ ...position, width: 'max-content', minWidth: 260, maxWidth: 'calc(100vw - 16px)' }}>{children}</div>, document.body)}
  </>
}

export function BreakdownRow({ label, value, colorClass }) {
  return <div className="flex items-baseline justify-between gap-6 py-1"><span className="text-inkMuted">{label}</span><span className={`text-right tabular-nums ${colorClass || 'text-ink'}`}><PrivateValue>{value}</PrivateValue></span></div>
}

export default function PositionLedger({ live, closed, loading = false, symbol }) {
  const data = loading ? null : live
  const pnl = data?.openPnl ?? closed?.totalNetPnl
  const pnlPct = data?.openPnlPct ?? closed?.pnlPct
  const risk = data?.stopNetExposure ?? (closed?.totalRisk > 0 ? -closed.totalRisk : null)
  const rows = [
    ['Qty', qty(data?.totalQty ?? closed?.totalQty), data?.accounts?.length > 0 && <>
      <div className="text-inkMuted mb-1">Account breakdown</div>
      {data.accounts.map(account => <BreakdownRow key={account.accountNumber} label={<AccountName accountNumber={account.accountNumber} />}
        value={`${qty(account.qty)} (${finite(account.allocationPct) ? Number(account.allocationPct).toFixed(2) + '%' : '—'})`} />)}
      <div className="text-inkMuted mt-1 text-[10px]">% of account equity</div>
    </>],
    ['Cost basis', money(data?.avgEntryPrice ?? closed?.avgEntry)],
    [closed ? 'Total P&L' : 'Open P&L', finite(pnl) ? `${signed(pnl)} (${percent(pnlPct)})` : '—', data && <>
      <div className="text-inkMuted mb-1">P&L breakdown</div>
      {[['Campaign P&L', data.campaignPnl], ['Realized P&L', data.realizedPnl], ['Broker Open P&L', data.brokerOpenPnl]].map(([label, value]) => <BreakdownRow key={label} label={label} value={signed(value)} colorClass={color(value)} />)}
    </>, color(pnl)],
    ['Risk', signed(risk), data && finite(risk) && <>
      <div className="text-inkMuted mb-1">Portfolio impact</div>
      <BreakdownRow label="Open position risk" value={signed(data.openPositionRisk)} colorClass={color(data.openPositionRisk)} />
      <BreakdownRow label="Open portfolio risk" value={percent(data.openPortfolioRiskPct)} colorClass={color(data.openPortfolioRiskPct)} />
      <BreakdownRow label="Initial risk" value={finite(data.initialRisk) ? signed(-data.initialRisk) : '—'} />
      <BreakdownRow label="Portfolio starting risk" value={finite(data.portfolioStartingRiskPct) ? percent(-data.portfolioStartingRiskPct) : '—'} />
    </>],
    ['R-multiple', finite(data?.rMultiple ?? closed?.rMultiple) ? `${(data?.rMultiple ?? closed?.rMultiple) >= 0 ? '+' : ''}${(data?.rMultiple ?? closed?.rMultiple).toFixed(2)}R` : '—'],
    ['Days in trade', finite(data?.daysInTrade ?? closed?.daysInTrade) ? `${data?.daysInTrade ?? closed?.daysInTrade}d` : '—'],
  ]
  return <section aria-label="Position ledger" aria-busy={loading} className="p-3 font-sans text-xs text-ink" key={symbol}>
    <dl className="flex flex-col gap-3">
      {rows.map(([label, value, details, colorClass]) => <div key={label} className="flex items-baseline justify-between gap-3">
        <dt className="text-inkMuted shrink-0">{label}</dt>
        <dd className={`min-w-0 text-right font-normal tabular-nums ${colorClass || ''}`}>
          {details ? <DetailValue key={`${symbol}-${label}`} label={label} value={value} colorClass={colorClass}>{details}</DetailValue> : ['Qty', 'R-multiple', 'Days in trade'].includes(label) ? value : <PrivateValue>{value}</PrivateValue>}
        </dd>
      </div>)}
    </dl>
  </section>
}
